/*
<summary>
   This functional component is all about rendering the Add Defective Item form.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik, useField } from 'formik';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IAddDefectiveItem } from '../../../models/forms/IAddUpdateDefectiveItem';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import { IDefectiveItemList } from '../../../models/response/IDefectiveItemResponse';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormTextArea from '../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import { DefectiveItemValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import Label from '../../../shared-components/label/label';
import { formatMessage } from '../../../translations/format-message';

/**
 * This function is used to render the Subject field with debounced autocomplete suggestions.
 * Subject is free text: the user can pick a suggestion or keep typing their own value, so this
 * only ever offers matches, it never forces one.
 */
const SubjectAutocompleteInput = observer(() => {
  const [field, meta, helpers] = useField('Subject');
  const { defectiveItemsStore } = useStore();
  const {
    GetDefectiveItemSubjectListService,
    resetSubjectSuggestions,
    subjectSuggestions,
  } = defectiveItemsStore;
  const [showSuggestions, setShowSuggestions] = useState(false);

  /**
   * This useEffect is used to fetch subject suggestions from the store, debounced as the user types.
   */
  useEffect(() => {
    const subject = (field.value || '').trim();
    if (!subject) {
      resetSubjectSuggestions();
      return;
    }
    const debounceTimer = setTimeout(() => {
      GetDefectiveItemSubjectListService(
        Number(getTenantIdFromLocalStorage()),
        subject
      );
    }, 100);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  return (
    <div className="defective-item-subject-field">
      <input
        {...field}
        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
        placeholder={formatMessage('type_to_search_subject')}
        autoComplete="off"
        maxLength={256}
        onFocus={() => setShowSuggestions(true)}
        onBlur={(e) => {
          field.onBlur(e);
          setTimeout(() => setShowSuggestions(false), 150);
        }}
      />
      {meta.touched && meta.error && (
        <div className="invalid-feedback d-block errorMessage">
          <FormattedMessage id={meta.error} />
        </div>
      )}
      {showSuggestions && subjectSuggestions.length > 0 && (
        <ul className="defective-item-subject-suggestions">
          {subjectSuggestions.map((subject) => (
            <li
              key={subject}
              onMouseDown={() => {
                helpers.setValue(subject);
                setShowSuggestions(false);
              }}
            >
              {subject}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

/**
 * This function is used to render the Add Defective Item form fields and its save/cancel actions.
 * @param initialValues: Initial values for the defective item form
 * @param isLoading: Flag indicating whether the save request is in progress
 * @param submitHandler: Function invoked with the form values on save
 * @param onModalClosed: Function invoked when the popup should close
 */
const AddDefectiveItemForm = ({
  id,
  initialValues,
  isLoading,
  submitHandler,
  onModalClosed,
}: IAddEditFormProps<IDefectiveItemList>) => {
  return (
    <Modal
      show={true}
      centered
      className="add-defective-item-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage
            id={id > 0 ? 'update_defective_item' : 'add_defective_item'}
          />
          <button
            onClick={onModalClosed}
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={DefectiveItemValidateSchema}
        validateOnBlur={false}
        onSubmit={submitHandler}
      >
        {() => (
          <Form className="user-form">
            <Modal.Body>
              <div className="row">
                <div className="col-6">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required
                      label="subject"
                    />
                    <SubjectAutocompleteInput />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required
                      label="item"
                    />
                    <Field
                      className="form-control"
                      name="Item"
                      type="text"
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={256}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required={false}
                      label="classification_1"
                    />
                    <Field
                      className="form-control"
                      name="Classification1"
                      type="text"
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={256}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required={false}
                      label="classification_2"
                    />
                    <Field
                      className="form-control"
                      name="Classification2"
                      type="text"
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={256}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required={false}
                      label="comments"
                    />
                    <Field
                      className="form-control"
                      name="Comments"
                      placeholder=""
                      type="text"
                      as={FormikFormTextArea}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center w-100">
                <Button
                  variant="light-danger"
                  className="me-2"
                  type="button"
                  onClick={onModalClosed}
                  disabled={isLoading}
                >
                  <FormattedMessage id="cancel" />
                </Button>
                <LoaderButton
                  type="submit"
                  isLoading={isLoading}
                  text="save"
                />
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddDefectiveItemForm;
